// PlanScoringPolicy v0 — P3 of the Plans & Reasons loop (score step).
//
// The moment Helix says "we recommend B", the ranking function becomes a legal
// surface. It is therefore a VERSIONED, DECLARED policy object — per-tenant
// DATA loaded from a registry (the three-fold law: core code here, which
// weights and ranks apply is tenant data) — never a model preference and never
// LLM arithmetic.
//
// Governed criteria only (the P3 spec's scoring rubric):
//   - $ impact vs the objective gap (common USD frame; coverage capped so
//     overshoot beyond the cap earns no extra score),
//   - time-to-impact class (closed vocabulary; missing window = named gap and
//     the floor contribution, never an invented default),
//   - certainty class (derived from evidence posture via max-degradation;
//     counted, never estimated — an invented "confidence %" is a WITHHOLD),
//   - constraint compliance (violations are marked and penalized, not
//     silently dropped — the violation is content).
//
// Scoring is deterministic and monotone by construction: every weight is
// non-negative and every criterion contribution is non-decreasing in the
// candidate's favor, so more $ + higher certainty + faster time-to-impact can
// never score lower (the dominance law, property-tested).

import type {
  PlanCandidateCertaintyClass,
  PlanCandidatePath,
  PlanTimeToImpactClass,
} from './plan-candidate-enumerator';
import {
  PLAN_CANDIDATE_CERTAINTY_ORDER,
  PLAN_TIME_TO_IMPACT_ORDER,
} from './plan-candidate-enumerator';

export type PlanScoringPolicyWeights = Readonly<{
  /** Weight on gap coverage (min(totalUsd / gapUsd, cap), normalized by cap). */
  gapCoverage: number;
  /** Weight on the certainty-class rank of the candidate (max-degradation). */
  certainty: number;
  /** Weight on the time-to-impact-class rank (unbound window = 0 floor). */
  timeToImpact: number;
  /** Penalty subtracted PER violated constraint flag. */
  constraintViolation: number;
}>;

export type PlanScoringPolicy = Readonly<{
  policyId: string;
  version: string;
  /** Policy bound on combination size, [1..3]. */
  maxLeversPerCandidate: number;
  /** Lever statuses admitted as candidates (tenant data, closed strings). */
  admittedLeverStatuses: readonly string[];
  weights: PlanScoringPolicyWeights;
  /** Coverage cap: overshoot beyond cap × gap earns no additional score. */
  gapCoverageCap: number;
  /** Rank per certainty class, each in [0, 1], monotone with class strength. */
  certaintyRank: Readonly<Record<PlanCandidateCertaintyClass, number>>;
  /** Rank per time class, each in [0, 1], monotone with class speed. */
  timeToImpactRank: Readonly<Record<PlanTimeToImpactClass, number>>;
  /** Who declared this policy (governed like ChainPolicy / MarketRegime). */
  declaredBy: string;
  evidenceRefId: string;
}>;

export type PlanScoringPolicyRefusal = Readonly<{
  code:
    | 'policy_weight_invalid'
    | 'policy_rank_invalid'
    | 'policy_rank_not_monotone'
    | 'policy_bound_invalid'
    | 'policy_identity_missing';
  label: string;
}>;

/**
 * Fail-closed policy validation. An invalid policy never scores anything —
 * the refusal is named and the composition withholds the recommendation.
 */
export function validatePlanScoringPolicy(
  policy: PlanScoringPolicy,
): PlanScoringPolicyRefusal | null {
  if (!policy.policyId.trim() || !policy.version.trim()) {
    return refusal(
      'policy_identity_missing',
      'PlanScoringPolicy must carry a policyId and version before it can rank.',
    );
  }
  if (!policy.declaredBy.trim() || !policy.evidenceRefId.trim()) {
    return refusal(
      'policy_identity_missing',
      'PlanScoringPolicy must be declared and evidence-bound (declaredBy + evidenceRefId).',
    );
  }
  const weights = [
    policy.weights.gapCoverage,
    policy.weights.certainty,
    policy.weights.timeToImpact,
    policy.weights.constraintViolation,
  ];
  if (weights.some((weight) => !Number.isFinite(weight) || weight < 0)) {
    return refusal(
      'policy_weight_invalid',
      'Every PlanScoringPolicy weight must be finite and non-negative (monotone scoring law).',
    );
  }
  if (!Number.isFinite(policy.gapCoverageCap) || policy.gapCoverageCap <= 0) {
    return refusal(
      'policy_weight_invalid',
      'gapCoverageCap must be finite and positive.',
    );
  }
  if (
    !Number.isInteger(policy.maxLeversPerCandidate) ||
    policy.maxLeversPerCandidate < 1 ||
    policy.maxLeversPerCandidate > 3
  ) {
    return refusal(
      'policy_bound_invalid',
      'maxLeversPerCandidate must be an integer in [1, 3].',
    );
  }
  for (const certaintyClass of PLAN_CANDIDATE_CERTAINTY_ORDER) {
    const rank = policy.certaintyRank[certaintyClass];
    if (!Number.isFinite(rank) || rank < 0 || rank > 1) {
      return refusal(
        'policy_rank_invalid',
        `certaintyRank.${certaintyClass} must be within [0, 1].`,
      );
    }
  }
  for (const timeClass of PLAN_TIME_TO_IMPACT_ORDER) {
    const rank = policy.timeToImpactRank[timeClass];
    if (!Number.isFinite(rank) || rank < 0 || rank > 1) {
      return refusal(
        'policy_rank_invalid',
        `timeToImpactRank.${timeClass} must be within [0, 1].`,
      );
    }
  }
  // Monotonicity: stronger certainty must never rank below weaker certainty;
  // faster time-to-impact must never rank below slower (dominance law).
  for (let i = 1; i < PLAN_CANDIDATE_CERTAINTY_ORDER.length; i += 1) {
    const weaker = PLAN_CANDIDATE_CERTAINTY_ORDER[i - 1];
    const stronger = PLAN_CANDIDATE_CERTAINTY_ORDER[i];
    if (policy.certaintyRank[stronger] < policy.certaintyRank[weaker]) {
      return refusal(
        'policy_rank_not_monotone',
        `certaintyRank must be monotone: ${stronger} ranks below ${weaker}.`,
      );
    }
  }
  for (let i = 1; i < PLAN_TIME_TO_IMPACT_ORDER.length; i += 1) {
    const faster = PLAN_TIME_TO_IMPACT_ORDER[i - 1];
    const slower = PLAN_TIME_TO_IMPACT_ORDER[i];
    if (policy.timeToImpactRank[faster] < policy.timeToImpactRank[slower]) {
      return refusal(
        'policy_rank_not_monotone',
        `timeToImpactRank must be monotone: ${faster} ranks below ${slower}.`,
      );
    }
  }
  return null;
}

export type PlanCandidateScoreParts = Readonly<{
  /** weights.gapCoverage × normalized capped coverage, ≥ 0. */
  gapCoverage: number;
  /** weights.certainty × certaintyRank[class], ≥ 0. */
  certainty: number;
  /** weights.timeToImpact × timeToImpactRank[class] (0 when unbound), ≥ 0. */
  timeToImpact: number;
  /** weights.constraintViolation × violatedCount, subtracted. */
  constraintPenalty: number;
}>;

export type PlanCandidateScore = Readonly<{
  candidateId: string;
  /** Deterministic rank, 1 = recommended-first. */
  rank: number;
  score: number;
  parts: PlanCandidateScoreParts;
  /** Coverage of the objective gap as a raw fraction (uncapped), or null. */
  gapCoverageFraction: number | null;
  constraintViolationIds: readonly string[];
}>;

export type ScorePlanCandidatesInput = Readonly<{
  policy: PlanScoringPolicy;
  candidates: readonly PlanCandidatePath[];
  objectiveGapUsd: number | null;
}>;

export type ScorePlanCandidatesResult =
  | Readonly<{
      status: 'scored';
      policyRef: string;
      /** Scores in rank order (rank 1 first). */
      scores: readonly PlanCandidateScore[];
    }>
  | Readonly<{ status: 'refused'; refusal: PlanScoringPolicyRefusal }>;

/**
 * Deterministic ranking under the declared policy. Ties break by totalUsd
 * (more first), then certainty rank, then candidateId (lexical) — fully
 * reproducible, content-addressed, no clock, no LLM.
 */
export function scorePlanCandidates(
  input: ScorePlanCandidatesInput,
): ScorePlanCandidatesResult {
  const invalid = validatePlanScoringPolicy(input.policy);
  if (invalid) return { status: 'refused', refusal: invalid };

  const maxTotalUsd = input.candidates.reduce(
    (max, candidate) => Math.max(max, candidate.totalUsd),
    0,
  );

  const scored = input.candidates.map((candidate) =>
    scoreCandidate(candidate, input.policy, input.objectiveGapUsd, maxTotalUsd),
  );

  const byId = new Map(
    input.candidates.map((candidate) => [candidate.candidateId, candidate]),
  );
  const ordered = [...scored].sort((left, right) => {
    if (right.score !== left.score) return right.score - left.score;
    const leftCandidate = byId.get(left.candidateId);
    const rightCandidate = byId.get(right.candidateId);
    const leftTotal = leftCandidate?.totalUsd ?? 0;
    const rightTotal = rightCandidate?.totalUsd ?? 0;
    if (rightTotal !== leftTotal) return rightTotal - leftTotal;
    const leftCertainty = leftCandidate
      ? input.policy.certaintyRank[leftCandidate.certaintyClass]
      : 0;
    const rightCertainty = rightCandidate
      ? input.policy.certaintyRank[rightCandidate.certaintyClass]
      : 0;
    if (rightCertainty !== leftCertainty) return rightCertainty - leftCertainty;
    return left.candidateId.localeCompare(right.candidateId);
  });

  return {
    status: 'scored',
    policyRef: `${input.policy.policyId}@${input.policy.version}`,
    scores: Object.freeze(
      ordered.map((score, index) =>
        Object.freeze({ ...score, rank: index + 1 }),
      ),
    ),
  };
}

function scoreCandidate(
  candidate: PlanCandidatePath,
  policy: PlanScoringPolicy,
  objectiveGapUsd: number | null,
  maxTotalUsd: number,
): PlanCandidateScore {
  const gapCoverageFraction =
    objectiveGapUsd !== null && objectiveGapUsd > 0
      ? candidate.totalUsd / objectiveGapUsd
      : null;

  // When the gap is bound: capped coverage normalized by the cap so the part
  // lands in [0, 1]. When no gap is bound: normalize by the largest candidate
  // in the set (still monotone in totalUsd within the scoring run).
  const coverageBasis =
    gapCoverageFraction !== null
      ? Math.min(gapCoverageFraction, policy.gapCoverageCap) /
        policy.gapCoverageCap
      : maxTotalUsd > 0
        ? candidate.totalUsd / maxTotalUsd
        : 0;

  const certaintyBasis = policy.certaintyRank[candidate.certaintyClass];
  const timeBasis =
    candidate.timeToImpactClass === null
      ? 0
      : policy.timeToImpactRank[candidate.timeToImpactClass];

  const violationIds = candidate.constraintFlags
    .filter((flag) => flag.status === 'violated')
    .map((flag) => flag.constraintId)
    .sort();

  const parts: PlanCandidateScoreParts = Object.freeze({
    gapCoverage: round6(policy.weights.gapCoverage * coverageBasis),
    certainty: round6(policy.weights.certainty * certaintyBasis),
    timeToImpact: round6(policy.weights.timeToImpact * timeBasis),
    constraintPenalty: round6(
      policy.weights.constraintViolation * violationIds.length,
    ),
  });

  return Object.freeze({
    candidateId: candidate.candidateId,
    rank: 0,
    score: round6(
      parts.gapCoverage +
        parts.certainty +
        parts.timeToImpact -
        parts.constraintPenalty,
    ),
    parts,
    gapCoverageFraction:
      gapCoverageFraction === null ? null : round6(gapCoverageFraction),
    constraintViolationIds: Object.freeze(violationIds),
  });
}

function refusal(
  code: PlanScoringPolicyRefusal['code'],
  label: string,
): PlanScoringPolicyRefusal {
  return Object.freeze({ code, label });
}

function round6(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}
